// EWM-860: group AI Advisory sidebar conversations by folder, deduping WITHIN each list.
//
// The legacy sidebar deduped across the entire conversation set with a single seenIds/seenTitles
// pair and then grouped. Because the server sorts LastMessageAt desc, recent unfoldered
// conversations were processed first and won; a foldered conversation whose normalized title
// collided with an earlier one was filtered out entirely, so its folder rendered fewer rows than
// its (server-computed) count badge. EWM-859 surfaced this by newly routing foldered conversations
// through the filter.
//
// Fix: dedupe per list (each folder + the unfoldered list independently), so a conversation in one
// list can never suppress a conversation in another. Empty / repetitive titles are no longer hidden
// here — buildDisplayTitle already substitutes a readable date label for them, so hiding the row
// only made the folder under-render.

/** Minimal shape the grouping needs; the sidebar's IConversationDisplay satisfies it structurally. */
export interface GroupableConversation {
  conversationId: string;
  rawTitle: string;
  folderId: string | null;
}

export interface GroupedConversations<T> {
  /** Foldered conversations keyed by folderId, in input (server-sorted) order. */
  byFolder: Map<string, T[]>;
  /** Top-level conversations with no folder, in input (server-sorted) order. */
  unfoldered: T[];
}

// Distinct from any real folderId (a GUID), so the unfoldered list gets its own dedupe scope.
const UNFOLDERED_KEY = '__unfoldered__';

/**
 * Split conversations into their folder lists + the unfoldered list, deduping by conversationId
 * and by normalized title independently within each list. A blank title is never used as a dedupe
 * key: multiple untitled conversations are legitimately distinct and each gets its own date label
 * downstream.
 */
export const groupConversationsByFolder = <T extends GroupableConversation>(
  conversations: T[],
): GroupedConversations<T> => {
  const byFolder = new Map<string, T[]>();
  const unfoldered: T[] = [];
  const seenIdsByList = new Map<string, Set<string>>();
  const seenTitlesByList = new Map<string, Set<string>>();

  const scoped = <V>(store: Map<string, Set<V>>, key: string): Set<V> => {
    let set = store.get(key);
    if (!set) {
      set = new Set<V>();
      store.set(key, set);
    }
    return set;
  };

  for (const c of conversations) {
    const listKey = c.folderId ?? UNFOLDERED_KEY;

    const seenIds = scoped(seenIdsByList, listKey);
    if (seenIds.has(c.conversationId)) continue;
    seenIds.add(c.conversationId);

    const normalizedTitle = c.rawTitle.trim().toLowerCase();
    if (normalizedTitle.length > 0) {
      const seenTitles = scoped(seenTitlesByList, listKey);
      if (seenTitles.has(normalizedTitle)) continue;
      seenTitles.add(normalizedTitle);
    }

    if (c.folderId) {
      const arr = byFolder.get(c.folderId) ?? [];
      arr.push(c);
      byFolder.set(c.folderId, arr);
    } else {
      unfoldered.push(c);
    }
  }

  return { byFolder, unfoldered };
};
